import { z } from 'zod';

// Request timeout (30s) for the embedded authorization calls.
const REQUEST_TIMEOUT_MS = 30_000;

/** Maximum number of resume advances to follow before giving up. */
export const MAX_RESUME_ITERATIONS = 10;

/**
 * HTTP-level failure from the embedded authorization endpoint. Lets callers
 * branch on the status code (e.g. 503 -> backend downtime).
 */
export class EmbeddedFlowHTTPError extends Error {
  readonly status: number;
  readonly statusText: string;

  constructor(status: number, statusText: string) {
    super(`Embedded flow HTTP error: ${status} ${statusText}`);
    this.name = 'EmbeddedFlowHTTPError';
    this.status = status;
    this.statusText = statusText;
  }
}

/**
 * Parameters for the embedded authorization start request. `intentId` is the
 * only optional field — the backend accepts an optional `intent_id`.
 */
type EmbeddedFlowStartParams = {
  baseUrl: string;
  clientId: string;
  scope: string;
  state: string;
  codeChallenge: string;
  webhookUri: string;
  intentId?: string;
};

/**
 * The interaction to settle next, which both endpoints return, read into the
 * shape `YonduOAuthInteraction.fromURIs` takes.
 */
const interactionSchema = z
  .object({
    authorization: z.string(),
    interaction: z.string(),
    oauth_interaction_start_uri: z.string(),
    oauth_interaction_finalize_uri: z.string(),
    oauth_authorization_resume_uri: z.string(),
  })
  .transform((body) => ({
    authorization: body.authorization,
    interaction: body.interaction,
    startUri: body.oauth_interaction_start_uri,
    finalizeUri: body.oauth_interaction_finalize_uri,
    resumeUri: body.oauth_authorization_resume_uri,
  }));

type EmbeddedInteractionUris = z.output<typeof interactionSchema>;

/** An OAuth error, which both endpoints return. */
const oauthErrorSchema = z
  .object({ error: z.string(), error_description: z.string() })
  .transform((body) => ({
    status: 'error' as const,
    error: body.error,
    errorDescription: body.error_description,
  }));

/** CollectionCompleted body of `GET /authorization/embedded/resume`. */
const completedSchema = z.object({ state: z.string() });

/**
 * Outcome of the embedded authorization start call: the first interaction and
 * its three URIs, or the OAuth error fields.
 */
export type EmbeddedFlowStartResult =
  | ({ status: 'success' } & EmbeddedInteractionUris)
  | z.output<typeof oauthErrorSchema>;

/** Outcome of advancing an embedded authorization via its resume URI. */
export type EmbeddedResumeResult =
  | ({ status: 'interactionSelected' } & EmbeddedInteractionUris)
  | { status: 'completed'; state: string }
  | z.output<typeof oauthErrorSchema>;

function buildRequestUrl(params: EmbeddedFlowStartParams): string {
  const url = new URL(`${params.baseUrl}/authorization/embedded/request`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('scope', params.scope);
  url.searchParams.set('state', params.state);
  url.searchParams.set('code_challenge', params.codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('webhook_uri', params.webhookUri);
  if (params.intentId) url.searchParams.set('intent_id', params.intentId);
  return url.toString();
}

/**
 * Aborts after the request timeout or when `signal` aborts. Combined by hand:
 * `AbortSignal.any` needs Safari 17.4.
 */
function withTimeout(signal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  if (!signal) return timeout;
  const combined = new AbortController();
  for (const source of [signal, timeout]) {
    if (source.aborted) combined.abort(source.reason);
    source.addEventListener('abort', () => combined.abort(source.reason), {
      once: true,
    });
  }
  return combined.signal;
}

/** GET a JSON body; gives up after the request timeout or when `signal` aborts. */
async function getJson(url: string, signal?: AbortSignal) {
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-cache',
    signal: withTimeout(signal),
  });
  let body: unknown;
  try {
    body = await response.json();
  } catch (e) {
    // A gateway's HTML error page: report the status, so a 503 reads as one.
    if (!response.ok) {
      throw new EmbeddedFlowHTTPError(response.status, response.statusText);
    }
    throw e;
  }
  return { response, body };
}

/**
 * driveEmbeddedFlow — performs ONLY the embedded authorization start:
 *   GET {baseUrl}/authorization/embedded/request?response_type=code&client_id=…
 *       &scope=…&state=…&code_challenge=…&code_challenge_method=S256[&intent_id=…]
 *
 * The start response returns the first interaction directly. Advancing the flow
 * afterwards is driven per-interaction via the resume helper.
 */
export async function driveEmbeddedFlow(
  params: EmbeddedFlowStartParams,
  signal: AbortSignal,
): Promise<EmbeddedFlowStartResult> {
  const { response, body } = await getJson(buildRequestUrl(params), signal);

  const error = oauthErrorSchema.safeParse(body);
  if (error.success) return error.data;

  if (!response.ok) {
    throw new EmbeddedFlowHTTPError(response.status, response.statusText);
  }

  const interaction = interactionSchema.safeParse(body);
  if (!interaction.success) {
    throw new Error(
      `[Ronan] Unexpected embedded/request response body: ${JSON.stringify(body)} — ${JSON.stringify(interaction.error.issues)}`,
    );
  }
  return { status: 'success', ...interaction.data };
}

/**
 * driveEmbeddedResume — advances an embedded authorization by fetching its
 * resume URI (in embedded mode the resume endpoint must be fetched, not
 * navigated to) and mapping the JSON outcome to a discriminated union:
 * InteractionSelected | CollectionCompleted | Error.
 */
export async function driveEmbeddedResume(
  resumeUri: string,
  signal?: AbortSignal,
): Promise<EmbeddedResumeResult> {
  const { response, body } = await getJson(resumeUri, signal);

  const interaction = interactionSchema.safeParse(body);
  if (interaction.success) {
    return { status: 'interactionSelected', ...interaction.data };
  }

  const error = oauthErrorSchema.safeParse(body);
  if (error.success) return error.data;

  const completed = completedSchema.safeParse(body);
  if (completed.success) {
    return { status: 'completed', state: completed.data.state };
  }

  throw new Error(
    `[Ronan] Unexpected embedded/resume response body (HTTP ${response.status} ${response.statusText}): ${JSON.stringify(body)}`,
  );
}
